import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Opt-in ambient drone — synthesized via Web Audio (no asset). Two sine
 * oscillators at 111 Hz + 222 Hz (a fifth apart), low-passed and gently
 * modulated. Never autoplays — the user must press the toggle. Preference
 * persists across visits but audio still starts only on explicit gesture
 * (browser autoplay policy).
 */
export function AmbientAudio() {
  const [on, setOn] = useState(false);
  const ctxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);

  const stop = useCallback(() => {
    const ctx = ctxRef.current;
    const gain = gainRef.current;
    if (!ctx || !gain) return;
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + 0.6);
    setTimeout(() => {
      try { ctx.close(); } catch { /* noop */ }
      ctxRef.current = null;
      gainRef.current = null;
    }, 700);
  }, []);

  const start = useCallback(async () => {
    if (ctxRef.current) return;
    type W = typeof window & { webkitAudioContext?: typeof AudioContext };
    const AC = window.AudioContext ?? (window as W).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    ctxRef.current = ctx;

    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    gainRef.current = master;

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 900;
    filter.Q.value = 0.6;
    filter.connect(master);

    const freqs = [111, 222, 333];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = i === 0 ? "sine" : "triangle";
      osc.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = i === 0 ? 0.14 : 0.06;
      osc.connect(g).connect(filter);
      osc.start();

      // subtle detune LFO
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.frequency.value = 0.05 + i * 0.03;
      lfoGain.gain.value = 0.4 + i * 0.2;
      lfo.connect(lfoGain).connect(osc.detune);
      lfo.start();
    });

    try { await ctx.resume(); } catch { /* noop */ }
    const now = ctx.currentTime;
    master.gain.linearRampToValueAtTime(0.35, now + 1.4);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const toggle = () => {
    const next = !on;
    setOn(next);
    try { window.localStorage.setItem("ka.ambient", next ? "1" : "0"); } catch { /* noop */ }
    if (next) void start(); else stop();
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      aria-label={on ? "Silence ambient tone" : "Play ambient tone"}
      title={on ? "Silence ambient tone" : "Play 111 Hz ambient tone"}
      className="fixed bottom-24 left-4 z-40 inline-flex items-center gap-2 rounded-full border hairline bg-charcoal/80 px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-md transition-colors hover:border-gold hover:text-gold-luminous focus:outline-none focus:ring-2 focus:ring-gold sm:bottom-6"
    >
      {on ? <Volume2 size={12} aria-hidden /> : <VolumeX size={12} aria-hidden />}
      {on ? "Tone" : "Silent"}
    </button>
  );
}
